import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Users, Briefcase, Trophy, CheckSquare, Calendar, Clock, Fingerprint, Inbox,
  RefreshCw, Save, Eye, UserRound, ShoppingCart, PhoneCall, MessageSquare,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { VISIBILITY_DOMAINS } from '@easyflow/shared';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { formatGrantDateTime } from '@/components/roles/grant-format';
import { apiClient } from '@/lib/api';
import type {
  VisibilityDomain,
  VisibilityPolicies,
  VisibilityPoliciesPut,
  VisibilityPoliciesResponse,
  VisibilityPolicy,
} from '@easyflow/shared';

interface DomainDef {
  key: VisibilityDomain;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface DomainGroup {
  id: string;
  label: string;
  domains: DomainDef[];
}

const DOMAIN_GROUPS: DomainGroup[] = [
  {
    id: 'commerciale',
    label: 'Commerciale',
    domains: [
      { key: 'crm', label: 'CRM · Lead', description: 'Lead assegnati alle persone visibili.', icon: Briefcase },
      { key: 'incentivazione', label: 'Incentivazione', description: 'Maturati, payout e prese visione delle gare.', icon: Trophy },
      { key: 'pos', label: 'POS · Operatori', description: 'Vendite e pratiche RMA/DOA per operatore. Chiusure di cassa, prima nota e corrispettivi restano per registratore/sede.', icon: ShoppingCart },
    ],
  },
  {
    id: 'operativita',
    label: 'Operatività',
    domains: [
      { key: 'tasks', label: 'Task', description: 'Task e board delle persone visibili.', icon: CheckSquare },
      { key: 'calendar', label: 'Calendario', description: 'Agende e appuntamenti delle persone visibili.', icon: Calendar },
    ],
  },
  {
    id: 'comunicazione',
    label: 'Comunicazione',
    domains: [
      { key: 'telephony', label: 'Telefonia', description: 'Chiamate e registrazioni degli interni delle persone visibili.', icon: PhoneCall },
      { key: 'messaging', label: 'Messaggistica', description: 'Conversazioni dei numeri e canali assegnati alle persone visibili.', icon: MessageSquare },
    ],
  },
  {
    id: 'hr',
    label: 'Risorse umane',
    domains: [
      { key: 'hr.shifts', label: 'HR · Turni', description: 'Pianificazione turni, copertura e competenze.', icon: Clock },
      { key: 'hr.attendance', label: 'HR · Presenze', description: 'Timbrature e anomalie.', icon: Fingerprint },
      { key: 'hr.requests', label: 'HR · Richieste', description: 'Ferie, permessi e altre richieste.', icon: Inbox },
    ],
  },
];

const POLICY_OPTIONS: ReadonlyArray<{
  value: VisibilityPolicy;
  label: string;
  hint: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  {
    value: 'matrioska',
    label: 'Matrioska',
    hint: 'Ognuno vede se stesso; i ruoli manager vedono le persone delle proprie sedi.',
    icon: UserRound,
  },
  {
    value: 'tutti_vedono',
    label: 'Tutti vedono',
    hint: 'Chi ha il permesso di vista vede tutte le persone del proprio recinto (solo lettura).',
    icon: Eye,
  },
];

const VISIBILITY_POLICIES_PATH = '/tenant/visibility-policies';

function defaultPolicies(): VisibilityPolicies {
  return Object.fromEntries(VISIBILITY_DOMAINS.map((d) => [d, 'matrioska'])) as VisibilityPolicies;
}

export default function PersonVisibilityPolicyTab() {
  const { toast } = useToast();
  const [saved, setSaved] = useState<VisibilityPolicies>(defaultPolicies);
  const [draft, setDraft] = useState<VisibilityPolicies>(defaultPolicies);
  const [updatedAt, setUpdatedAt] = useState<VisibilityPoliciesResponse['updated_at']>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiClient.get<VisibilityPoliciesResponse>(VISIBILITY_POLICIES_PATH);
      if (!res.success || !res.data) throw new Error(res.error ?? 'Risposta non valida');
      const merged = { ...defaultPolicies(), ...res.data.policies };
      setSaved(merged);
      setDraft(merged);
      setUpdatedAt(res.data.updated_at);
    } catch (error) {
      toast({ title: 'Errore', description: getApiErrorMessage(error) || 'Impossibile caricare le policy di visibilità', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const changedDomains = useMemo(
    () => VISIBILITY_DOMAINS.filter((d) => draft[d] !== saved[d]),
    [draft, saved],
  );
  const isDirty = changedDomains.length > 0;

  const handleSave = async () => {
    if (!isDirty) return;
    setIsSaving(true);
    try {
      const body: VisibilityPoliciesPut = {
        policies: Object.fromEntries(changedDomains.map((d) => [d, draft[d]])),
      };
      const res = await apiClient.put<VisibilityPoliciesResponse>(VISIBILITY_POLICIES_PATH, body);
      if (!res.success || !res.data) throw new Error(res.error ?? 'Risposta non valida');
      const merged = { ...defaultPolicies(), ...res.data.policies };
      setSaved(merged);
      setDraft(merged);
      setUpdatedAt(res.data.updated_at);
      toast({ title: 'Policy aggiornate', description: `${changedDomains.length} ${changedDomains.length === 1 ? 'dominio aggiornato' : 'domini aggiornati'}.` });
    } catch (error) {
      toast({ title: 'Errore', description: getApiErrorMessage(error) || 'Salvataggio non riuscito', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const setGroup = (group: DomainGroup, policy: VisibilityPolicy) => {
    setDraft((prev) => {
      const next = { ...prev };
      for (const d of group.domains) next[d.key] = policy;
      return next;
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Chi vede chi
        </CardTitle>
        <CardDescription>
          Per ogni area decidi quante persone rientrano nella vista di chi ci lavora. Il recinto
          (sedi e aziende) resta sempre applicato; la gestione (creare, modificare, approvare)
          segue sempre la matrioska. Le eccezioni per singola persona si impostano dalla scheda utente.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              {POLICY_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
                  <opt.icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 text-sm">
                    <p className="font-medium">{opt.label}</p>
                    <p className="text-muted-foreground">{opt.hint}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-5">
              {DOMAIN_GROUPS.map((group) => (
                <section key={group.id} className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</h3>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <span className="hidden sm:inline">Imposta tutto:</span>
                      {POLICY_OPTIONS.map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => setGroup(group, opt.value)}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="divide-y rounded-lg border">
                    {group.domains.map((domain) => {
                      const current = draft[domain.key];
                      const changed = current !== saved[domain.key];
                      return (
                        <div key={domain.key} className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex min-w-0 items-start gap-3">
                            <domain.icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium">{domain.label}</p>
                                {changed && <Badge variant="outline" className="h-5 text-[10px]">modificato</Badge>}
                              </div>
                              <p className="text-xs text-muted-foreground">{domain.description}</p>
                            </div>
                          </div>
                          <div
                            role="radiogroup"
                            aria-label={`Policy ${domain.label}`}
                            className="inline-flex shrink-0 self-start rounded-md border bg-muted/40 p-0.5 sm:self-auto"
                          >
                            {POLICY_OPTIONS.map((opt) => {
                              const active = current === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  role="radio"
                                  aria-checked={active}
                                  onClick={() => setDraft((prev) => ({ ...prev, [domain.key]: opt.value }))}
                                  className={cn(
                                    'inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                    active
                                      ? 'bg-background text-foreground shadow-sm'
                                      : 'text-muted-foreground hover:text-foreground',
                                  )}
                                >
                                  <opt.icon className="h-3.5 w-3.5" />
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>

            <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-muted-foreground">
                {updatedAt
                  ? `Ultimo aggiornamento: ${formatGrantDateTime(updatedAt)}`
                  : 'Nessuna policy personalizzata: tutte le aree usano la matrioska.'}
              </p>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" disabled={!isDirty || isSaving} onClick={() => setDraft(saved)}>
                  Annulla modifiche
                </Button>
                <Button type="button" size="sm" disabled={!isDirty || isSaving} onClick={() => { void handleSave(); }}>
                  {isSaving
                    ? <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Salvataggio...</>
                    : <><Save className="mr-2 h-4 w-4" />Salva {isDirty ? `(${changedDomains.length})` : ''}</>}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
